import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { FiUserPlus, FiShield, FiArrowRight } from 'react-icons/fi';
import { useTenant } from '../components/Layout';
import { getGovernanceFramework } from '../api';

const PILLAR_COLORS = {
  identity_access: '#4f46e5',
  management_structure: '#7c3aed',
  networking_connectivity: '#2563eb',
  security_compliance: '#ef4444',
  governance_policy: '#f59e0b',
  monitoring_observability: '#10b981',
  operations_resilience: '#0891b2',
  devops_automation: '#8b5cf6',
};

const RISK_STYLE = {
  mature: { bg: '#dcfce7', color: '#166534', label: 'Mature' },
  moderate: { bg: '#fef3c7', color: '#92400e', label: 'Moderate Risk' },
  high: { bg: '#fee2e2', color: '#991b1b', label: 'High Risk' },
};

export default function Overview() {
  const { selectedTenantId, tenants } = useTenant();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    if (!selectedTenantId || tenants.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getGovernanceFramework(selectedTenantId)
      .then(res => { setAssessment(res.data); setLoading(false); })
      .catch(() => { setAssessment(null); setLoading(false); });
  }, [selectedTenantId, tenants.length]);

  if (tenants.length === 0) {
    return (
      <div>
        <div className="page-header">
          <h1>Welcome to Landing Zone Intelligence</h1>
          <p>Get started by onboarding your first cloud tenant</p>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '3rem 2rem', background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)', border: '2px solid #e0e7ff' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', margin: '0 auto 1.25rem', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiShield style={{ fontSize: '2rem', color: '#fff' }} />
          </div>
          <h2 style={{ marginBottom: '0.75rem', color: '#1f2937' }}>No Tenants Onboarded</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem', maxWidth: 480, margin: '0 auto 1.5rem', lineHeight: 1.6 }}>
            Onboard your cloud tenant to start the Landing Zone Governance Assessment across 8 pillars with 46 controls.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/onboarding')} style={{ fontSize: '1rem', padding: '0.75rem 2rem', borderRadius: 10, boxShadow: '0 4px 14px rgba(79, 70, 229, 0.3)', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiUserPlus />
            Onboard Your First Tenant
            <FiArrowRight />
          </button>
        </div>
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ color: '#374151', marginBottom: '1rem', fontSize: '1.1rem' }}>Assessment Pillars</h3>
          <div className="grid-4">
            {[
              { icon: '🔐', title: 'Identity & Access', desc: 'RBAC, PIM, MFA, Conditional Access' },
              { icon: '🏗️', title: 'Management Structure', desc: 'Subscriptions, tagging, naming' },
              { icon: '🌐', title: 'Networking', desc: 'Hub-spoke, firewall, private endpoints' },
              { icon: '🛡️', title: 'Security & Compliance', desc: 'Defender, encryption, baselines' },
              { icon: '📋', title: 'Governance & Policy', desc: 'Cloud policies, cost, resource locks' },
              { icon: '📊', title: 'Monitoring', desc: 'Centralized logging, alerts, SIEM' },
              { icon: '⚡', title: 'Operations', desc: 'Backup, DR, availability zones' },
              { icon: '🔄', title: 'DevOps & Automation', desc: 'IaC, CI/CD, drift detection' },
            ].map((p, i) => (
              <div key={i} className="card" style={{ padding: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{p.icon}</div>
                <h4 style={{ fontSize: '0.8rem', marginBottom: '0.25rem', color: '#1f2937' }}>{p.title}</h4>
                <p style={{ fontSize: '0.7rem', color: '#6b7280', margin: 0 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading-container"><div className="spinner" /><p>Loading assessment data...</p></div>;

  const pillars = assessment?.pillars || {};
  const pillarEntries = Object.entries(pillars);
  const overallMaturity = assessment?.overall_maturity ?? 0;
  const overallRisk = assessment?.overall_risk ?? 'high';
  const totalItems = assessment?.total_items ?? 0;
  const totalPassed = assessment?.passed ?? 0;
  const riskStyle = RISK_STYLE[overallRisk] || RISK_STYLE.high;

  const radarData = pillarEntries.map(([, p]) => ({
    pillar: p.name.replace('&', '\n&').split(' ').slice(0, 2).join(' '),
    score: p.maturity_score,
    fullMark: 5,
  }));

  const totalFailed = pillarEntries.reduce((sum, [, p]) => sum + (p.failed || 0), 0);
  const totalWarnings = pillarEntries.reduce((sum, [, p]) => sum + (p.warnings || 0), 0);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>LZ Assessment Overview</h1>
          <p>Landing zone governance health across 8 pillars</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/gov-framework')} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          View Full Assessment <FiArrowRight />
        </button>
      </div>

      <div className="grid-4" style={{ marginBottom: '1.5rem' }}>
        <div className="metric-card">
          <div className="metric-label">Overall Maturity</div>
          <div className="metric-value" style={{ color: overallMaturity >= 4 ? '#10b981' : overallMaturity >= 3 ? '#f59e0b' : '#ef4444' }}>
            {overallMaturity}<span style={{ fontSize: '0.9rem', color: '#9ca3af' }}>/5</span>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Risk Level</div>
          <div style={{ marginTop: '0.25rem' }}>
            <span style={{ padding: '4px 14px', borderRadius: 10, fontSize: '0.9rem', fontWeight: 700, background: riskStyle.bg, color: riskStyle.color }}>
              {riskStyle.label}
            </span>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Controls Passed</div>
          <div className="metric-value" style={{ color: '#10b981' }}>
            {totalPassed}<span style={{ fontSize: '0.9rem', color: '#9ca3af' }}>/{totalItems}</span>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Issues Found</div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#ef4444' }}>{totalFailed}</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>Critical</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b' }}>{totalWarnings}</div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>Warnings</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '1.5rem' }}>
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Maturity Radar</h3>
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Radar dataKey="score" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.2} strokeWidth={2} />
                <Tooltip formatter={(value) => [`${value}/5`, 'Maturity']} />
              </RadarChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#9ca3af', textAlign: 'center', padding: '3rem' }}>No assessment data. Run a sync to start.</p>}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Pillar Scores</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {pillarEntries.map(([key, p]) => {
              const pct = (p.maturity_score / 5) * 100;
              const color = PILLAR_COLORS[key] || '#6b7280';
              return (
                <div key={key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151' }}>{p.name}</span>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color }}>{p.maturity_score}/5</span>
                  </div>
                  <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.5s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '1rem' }}>Pillar Summary</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Pillar</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Score</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Risk</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Passed</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Warnings</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', fontSize: '0.7rem', textTransform: 'uppercase', color: '#6b7280' }}>Failed</th>
              </tr>
            </thead>
            <tbody>
              {pillarEntries.map(([key, p]) => {
                const rs = RISK_STYLE[p.risk_level] || RISK_STYLE.high;
                return (
                  <tr key={key} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => navigate('/gov-framework')}>
                    <td style={{ padding: '10px', fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: 4, height: 28, borderRadius: 2, background: PILLAR_COLORS[key] || '#6b7280' }} />
                        {p.name}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: PILLAR_COLORS[key] }}>{p.maturity_score}/5</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600, background: rs.bg, color: rs.color }}>{rs.label}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>{p.passed}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: '#f59e0b', fontWeight: 600 }}>{p.warnings}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: '#ef4444', fontWeight: 600 }}>{p.failed}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
